
import { supabase, isSupabaseConfigured } from './supabase';

export interface ConnectionTestResult {
  configStatus: 'missing' | 'present';
  connectionStatus: 'untested' | 'success' | 'error';
  permissionsStatus: 'untested' | 'success' | 'error';
  error?: string;
  details?: string;
}

/**
 * Tests the connection to Supabase and returns diagnostic information
 */
export async function testSupabaseConnection(): Promise<ConnectionTestResult> {
  const result: ConnectionTestResult = {
    configStatus: 'missing',
    connectionStatus: 'untested',
    permissionsStatus: 'untested',
  };
  
  // Step 1: Check if configuration exists
  const isConfigured = isSupabaseConfigured();
  result.configStatus = isConfigured ? 'present' : 'missing';
  
  if (!isConfigured) {
    result.error = 'Supabase configuration is missing';
    result.details = 'Check that VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in Cloudflare Pages environment variables.';
    return result;
  }
  
  try {
    // Step 2: Test basic connectivity with a simple ping
    const { error: pingError } = await supabase.from('subscribers').select('count').limit(0);
    
    if (pingError) {
      result.connectionStatus = 'error';
      result.error = `Connection error: ${pingError.message}`;
      
      if (pingError.message?.includes('JWT') || pingError.message?.includes('key')) {
        result.details = 'This appears to be an authentication issue. Your API keys may be incorrect.';
      } else if (pingError.message?.includes('network')) {
        result.details = 'This appears to be a network issue. Supabase might be unreachable from your environment.';
      } else if (pingError.message?.includes('permission') || pingError.message?.includes('policy')) {
        result.details = 'This appears to be a permissions issue. Check your Row Level Security (RLS) policies.';
      }
      
      return result;
    }
    
    result.connectionStatus = 'success';
    
    // Step 3: Test permissions by attempting to insert a dummy record (with a transaction to roll it back)
    try {
      await supabase.rpc('test_permission', {});
      result.permissionsStatus = 'success';
    } catch (error: any) {
      console.error('Permission test error:', error);
      result.permissionsStatus = 'error';
      result.details = `Permission test failed: ${error?.message || 'Unknown error'}`;
    }
    
    return result;
  } catch (error: any) {
    result.connectionStatus = 'error';
    result.error = `Unexpected error: ${error?.message || 'Unknown error'}`;
    console.error('Supabase connection test error:', error);
    return result;
  }
}
